import { type UserRole, canAdminister, canManageUser } from '@cult/domain';
import { type AdminUser, SORT_ORDERS, USER_SORTS, type UserOrder, type UserSort } from '@cult/schemas';
import { App as AntApp, Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import dayjs from 'dayjs';
import { DataTable, RoleChip, UserAvatar } from '../../../../../components';
import { useUpdateUser } from '../../hooks';
import styles from './UsersTable.module.scss';

const { Text } = Typography;

type Props = {
  users: AdminUser[];
  loading: boolean;
  currentUserId: string;
  /** Role of the signed-in admin — gates which row actions are available. */
  currentUserRole: UserRole;
  onEdit: (user: AdminUser) => void;
  sort: UserSort;
  order: UserOrder;
  onSortChange: (sort: UserSort, order: UserOrder) => void;
  defaultSort?: UserSort;
  defaultOrder?: UserOrder;
  pagination: TablePaginationConfig;
};

const antOrderFor = (active: boolean, order: UserOrder): 'ascend' | 'descend' | null => {
  if (!active) return null;
  return order === SORT_ORDERS.ASC ? 'ascend' : 'descend';
};

export const UsersTable = ({
  users,
  loading,
  currentUserId,
  currentUserRole,
  onEdit,
  sort,
  order,
  onSortChange,
  defaultSort,
  defaultOrder,
  pagination,
}: Props) => {
  const update = useUpdateUser();
  const { message } = AntApp.useApp();

  const toggleActive = (u: AdminUser) => {
    update.mutate(
      { id: u.id, patch: { isActive: !u.isActive } },
      {
        onSuccess: () => message.success(`${u.isActive ? 'Deactivated' : 'Activated'} ${u.displayName}`),
        onError: (err) => message.error(err.message),
      },
    );
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (_v, u) => (
        <Space size={8}>
          <UserAvatar size={24} avatarUrl={u.avatarUrl} />
          <Text>{u.displayName}</Text>
          {u.id === currentUserId ? <RoleChip variant="member" label="You" style={{ fontSize: 11 }} /> : null}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => <Text type="secondary">{v}</Text>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 140,
      render: (v: AdminUser['role']) => <RoleChip variant={v} />,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 110,
      align: 'right',
      sorter: true,
      sortOrder: antOrderFor(sort === USER_SORTS.BALANCE, order),
      sortDirections: ['descend', 'ascend'],
      render: (v: AdminUser['balance']) =>
        typeof v === 'number' ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</Text> : null,
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: true,
      sortOrder: antOrderFor(sort === USER_SORTS.JOINED, order),
      sortDirections: ['descend', 'ascend'],
      render: (v: string) => (
        <Tooltip title={dayjs(v).format('MMM D, YYYY h:mm A')}>
          <Text type="secondary">{dayjs(v).fromNow()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 140,
      sorter: true,
      sortOrder: antOrderFor(sort === USER_SORTS.LAST_SEEN, order),
      sortDirections: ['descend', 'ascend'],
      render: (v: AdminUser['lastSeenAt']) =>
        v ? (
          <Tooltip title={dayjs(v).format('MMM D, YYYY h:mm A')}>
            <Text type="secondary">{dayjs(v).fromNow()}</Text>
          </Tooltip>
        ) : (
          <Tooltip title="Hasn't opened the site since last-seen tracking began">
            <Text type="secondary">—</Text>
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      align: 'right',
      render: (_v, u) => {
        const isSelf = u.id === currentUserId;
        // Only act on users ranked strictly below; editing roles additionally requires admin privileges.
        const canManage = !isSelf && canManageUser(currentUserRole, u.role);
        const canEditRole = canManage && canAdminister(currentUserRole);
        if (!canManage && !canEditRole) return null;
        return (
          <Space>
            {canManage ? (
              <Popconfirm
                title={`${u.isActive ? 'Deactivate' : 'Activate'} ${u.displayName}?`}
                description={u.isActive ? 'They will lose sign-in access.' : 'They will regain sign-in access.'}
                okText={u.isActive ? 'Deactivate' : 'Activate'}
                okButtonProps={{ danger: u.isActive }}
                onConfirm={() => toggleActive(u)}
              >
                <Button
                  size="small"
                  danger={u.isActive}
                  className={u.isActive ? undefined : 'btn-success'}
                  loading={update.isPending && update.variables?.id === u.id}
                >
                  {u.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </Popconfirm>
            ) : null}
            {canEditRole ? (
              <Button size="small" onClick={() => onEdit(u)}>
                Edit
              </Button>
            ) : null}
          </Space>
        );
      },
    },
  ];

  const handleChange: TableProps<AdminUser>['onChange'] = (_pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    // s.order is undefined when the user clicks a third time to clear sorting → reset to defaults.
    if (!s?.order) {
      onSortChange(defaultSort ?? USER_SORTS.JOINED, defaultOrder ?? SORT_ORDERS.DESC);
      return;
    }
    const SORT_BY_COLUMN: Record<string, UserSort> = {
      balance: USER_SORTS.BALANCE,
      lastSeenAt: USER_SORTS.LAST_SEEN,
    };
    const nextSort: UserSort = SORT_BY_COLUMN[String(s.columnKey)] ?? USER_SORTS.JOINED;
    const nextOrder: UserOrder = s.order === 'ascend' ? SORT_ORDERS.ASC : SORT_ORDERS.DESC;
    if (nextSort !== sort || nextOrder !== order) onSortChange(nextSort, nextOrder);
  };

  return (
    <DataTable<AdminUser>
      columns={columns}
      dataSource={users}
      loading={loading}
      onChange={handleChange}
      pagination={pagination}
      onRow={(u) => ({ className: u.isActive ? undefined : styles.inactiveRow })}
    />
  );
};
